import React, { useState } from "react";

import {
  CloseOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Button, Modal } from "antd";
import { toast } from "react-toastify";

import StyledTooltip from "../../../Common/UIComponents/StyledTooltip";
import {
  buildResolveSampleIssueSamplePayload,
  normalizeIssueType,
} from "./submissionIssueUtils";

import { resolveSampleIssues } from "../../../../actions/sampleSubmission";

import "./SampleIssueActionModal.css";

import { ReactComponent as DeleteBin } from "../../../../assets/images/sample-submission/DeleteBin.svg";
import { ReactComponent as ExpiryInfoCircleOutlined } from "../../../../assets/images/sample-submission/ExpiryInfoCircleOutlined.svg";
import { ReactComponent as RectangleLine } from "../../../../assets/images/sample-submission/RectangleLine.svg";
import { ReactComponent as TestTube } from "../../../../assets/images/sample-submission/TestTube.svg";

const FLOW_STEP = {
  REQUIRED_ACTION: "required_action",
  DELETE_CONFIRM: "delete_confirm",
  SUCCESS: "success",
};

const ISSUE_ALERT_COPY = {
  missing: {
    title: "Submission is in drafts",
    body: "Your sample was marked as Missing by the lab. The submission has been converted back to Draft because testing cannot begin without the sample.",
  },
  damaged: {
    title: "Submission is in drafts",
    body: "Your sample was marked as Damaged by the lab. The submission has been converted back to Draft because testing cannot begin without the sample.",
  },
};

export default function SampleIssueActionModal({
  open,
  submission,
  issueSample,
  onClose,
  onEditSample,
  onSuccess,
  prepareIssueSampleForResolve,
}) {
  const [step, setStep] = useState(FLOW_STEP.REQUIRED_ACTION);
  const [submitting, setSubmitting] = useState(false);

  const issueType = normalizeIssueType(issueSample?.issue_type);
  const alertCopy = ISSUE_ALERT_COPY[issueType] || ISSUE_ALERT_COPY.missing;
  const headerMetaParts = [submission?.submit_name, issueSample?.sample_id].filter(Boolean);
  const headerMetaText = headerMetaParts.join(" | ");

  const resetAndClose = () => {
    setStep(FLOW_STEP.REQUIRED_ACTION);
    setSubmitting(false);
    onClose();
  };

  const handleYes = async () => {
    if (typeof onEditSample === "function") {
      setSubmitting(true);
      try {
        await onEditSample(submission, issueSample);
      } finally {
        setSubmitting(false);
      }
    }
    resetAndClose();
  };

  const handleNo = (event) => {
    event.stopPropagation();
    setStep(FLOW_STEP.DELETE_CONFIRM);
  };

  const handleConfirmDelete = async () => {
    if (!submission || !issueSample?.sample_id) return;

    setSubmitting(true);
    let sampleDetails = issueSample;
    if (typeof prepareIssueSampleForResolve === "function") {
      sampleDetails = await prepareIssueSampleForResolve(submission, issueSample);
    }
    const sampleForApi = buildResolveSampleIssueSamplePayload(
      issueSample,
      submission,
      sampleDetails,
    );
    if (!sampleForApi.sample_id) {
      setSubmitting(false);
      return;
    }

    const { success, message } = await resolveSampleIssues({
      action: "delete",
      submission_id: submission.submit_id,
      sample: sampleForApi,
    });
    setSubmitting(false);

    if (success) {
      setStep(FLOW_STEP.SUCCESS);
      if (typeof onSuccess === "function") onSuccess();
      return;
    }
    toast.error(message);
  };

  const renderRequiredAction = () => (
    <>
      <div className="SampleIssueActionModal__Header">
        <RectangleLine aria-hidden />
        <div>
          <h3>Required Action</h3>
          {headerMetaText && (
            <StyledTooltip title={headerMetaText} placement="top">
              <p className="SampleIssueActionModal__HeaderMeta">
                {headerMetaText}
              </p>
            </StyledTooltip>
          )}
        </div>
      </div>
      <div className="SampleIssueActionModal__Alert">
        <ExpiryInfoCircleOutlined className="SampleIssueActionModal__AlertIcon" aria-hidden />
        <div>
          <p className="SampleIssueActionModal__AlertTitle">{alertCopy.title}</p>
          <p className="SampleIssueActionModal__AlertText">{alertCopy.body}</p>
        </div>
      </div>
      <p className="SampleIssueActionModal__Question">Are you planning to send the sample for testing?</p>
      <div className="SampleIssueActionModal__Options">
        <button type="button" className="SampleIssueActionModal__Option" onClick={handleYes} disabled={submitting}>
          <TestTube aria-hidden />
          <span className="SampleIssueActionModal__OptionText">Yes, I&apos;ll send the sample</span>
          <RightOutlined aria-hidden />
        </button>
        <button type="button" className="SampleIssueActionModal__Option" onClick={handleNo} disabled={submitting}>
          <DeleteBin aria-hidden />
          <span className="SampleIssueActionModal__OptionText">No, delete this sample submission</span>
          <RightOutlined aria-hidden />
        </button>
      </div>
    </>
  );

  const renderDeleteConfirm = () => (
    <>
      <div className="SampleIssueActionModal__Header SampleIssueActionModal__Header--plain">
        <h3>Delete Sample</h3>
      </div>
      <p className="SampleIssueActionModal__DeleteText">
        Are you sure you want to permanently delete this sample? This action is irreversible.
      </p>
      <div className="SampleIssueActionModal__Footer">
        <Button type="default" onClick={() => setStep(FLOW_STEP.REQUIRED_ACTION)}>
          Cancel
        </Button>
        <Button type="primary" loading={submitting} onClick={handleConfirmDelete}>
          Delete
        </Button>
      </div>
    </>
  );

  const renderSuccess = () => (
    <div className="SampleIssueActionModal__Success">
      <span className="SampleIssueActionModal__SuccessIcon" aria-hidden />
      <h4>Sample successfully deleted</h4>
      <Button type="primary" onClick={resetAndClose}>
        Done
      </Button>
    </div>
  );

  if (!submission || !issueSample) return null;

  return (
    <Modal
      title=""
      open={open}
      onCancel={resetAndClose}
      footer={null}
      centered
      destroyOnClose
      width={step === FLOW_STEP.SUCCESS ? 420 : 520}
      wrapClassName="SampleIssueActionModal"
      closeIcon={<CloseOutlined />}
    >
      <div className="SampleIssueActionModal__Body">
        {step === FLOW_STEP.REQUIRED_ACTION && renderRequiredAction()}
        {step === FLOW_STEP.DELETE_CONFIRM && renderDeleteConfirm()}
        {step === FLOW_STEP.SUCCESS && renderSuccess()}
      </div>
    </Modal>
  );
}
